import { useEffect, useRef, useState } from "react";
import { Icon, type IconName } from "./Icon.js";

export interface MenuItem {
  label: string;
  icon?: IconName;
  danger?: boolean;
  onClick: () => void;
}

interface ContextMenuState {
  x: number;
  y: number;
  items: MenuItem[];
}

/**
 * A lightweight right-click / trigger menu. Use the returned `open` to show it
 * from an event, and render `menu` somewhere near the root.
 */
export function useContextMenu() {
  const [state, setState] = useState<ContextMenuState | null>(null);

  const open = (event: { clientX: number; clientY: number; preventDefault: () => void }, items: MenuItem[]) => {
    event.preventDefault();
    setState({ x: event.clientX, y: event.clientY, items });
  };

  const close = () => setState(null);

  const menu = state ? <ContextMenuView state={state} onClose={close} /> : null;

  return { open, close, menu };
}

function ContextMenuView({ state, onClose }: { state: ContextMenuState; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: state.x, y: state.y });

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  // Keep the menu on-screen.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let { x, y } = state;
    if (x + rect.width > window.innerWidth - 8) x = window.innerWidth - rect.width - 8;
    if (y + rect.height > window.innerHeight - 8) y = window.innerHeight - rect.height - 8;
    setPos({ x, y });
  }, [state]);

  return (
    <div ref={ref} className="context-menu glass" style={{ left: pos.x, top: pos.y }} role="menu">
      {state.items.map((item, i) => (
        <button
          key={i}
          className={`context-item ${item.danger ? "context-item-danger" : ""}`}
          role="menuitem"
          onClick={() => {
            item.onClick();
            onClose();
          }}
        >
          {item.icon && <Icon name={item.icon} size={14} />}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}
